import { Controller } from "@hotwired/stimulus"
import { csrfToken } from "../utils/csrf_token"
import { showToast } from "../utils/show_toast"

export default class extends Controller {
  static values = { url: String }
  static targets = ["swatch", "toggle", "panel"]

  connect() {
    this.repositionHandler = this.repositionIfOpen.bind(this)
    this.clickOutsideHandler = this.clickOutside.bind(this)
    this.escapeHandler = this.closeOnEscape.bind(this)
  }

  disconnect() {
    this.teardownPanelListeners()
  }

  select(event) {
    const color = event.currentTarget.dataset.ownerSidebarColorKey
    if (!color) {
      this.hideOwnerSpinner()
      return
    }

    const previousColor = this.element.dataset.ownerSidebarColor
    if (color === previousColor) {
      this.hideOwnerSpinner()
      this.closePanelAfterSelect(event)
      return
    }

    this.applyColor(color)
    this.closePanelAfterSelect(event)
    this.persist(color, previousColor)
  }

  togglePanel(event) {
    event.preventDefault()
    event.stopPropagation()
    if (!this.hasPanelTarget) return

    if (this.panelTarget.classList.contains("is-closed")) {
      this.openPanel()
    } else {
      this.closePanel()
    }
  }

  clickOutside(event) {
    if (!this.panelOpen()) return
    if (this.panelTarget.contains(event.target)) return
    if (this.hasToggleTarget && this.toggleTarget.contains(event.target)) return
    this.closePanel()
  }

  closeOnEscape(event) {
    if (event.key !== "Escape") return
    if (!this.panelOpen()) return
    event.preventDefault()
    this.closePanel({ restoreFocus: true })
  }

  syncSwatches(color) {
    this.swatchTargets.forEach((el) => {
      const selected = el.dataset.ownerSidebarColorKey === color
      el.classList.toggle("is-selected", selected)
      el.setAttribute("aria-pressed", String(selected))
    })
  }

  persist(color, previousColor) {
    if (!this.urlValue) {
      this.hideOwnerSpinner()
      return
    }

    fetch(this.urlValue, {
      method: "PATCH",
      headers: {
        "X-CSRF-Token": csrfToken() || "",
        Accept: "application/json",
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ owner_sidebar_color: color })
    }).then((response) => {
      if (response.ok) {
        showToast("Left nav color saved")
      } else {
        this.applyColor(previousColor)
        showToast("Failed to save left nav color", "error")
      }
    }).catch(() => {
      this.applyColor(previousColor)
      showToast("Failed to save left nav color", "error")
    }).finally(() => {
      this.hideOwnerSpinner()
    })
  }

  applyColor(color) {
    this.element.dataset.ownerSidebarColor = color
    this.syncSwatches(color)
  }

  closePanelAfterSelect(event) {
    if (this.hasPanelTarget && this.panelTarget.contains(event.currentTarget)) {
      this.closePanel({ restoreFocus: true })
    }
  }

  hideOwnerSpinner() {
    const root = document.getElementById("owner-dashboard-root")
    if (!root) return

    this.application.getControllerForElementAndIdentifier(root, "spinner-overlay")?.hide()
  }

  openPanel() {
    this.panelTarget.classList.remove("is-closed")
    if (this.hasToggleTarget) this.toggleTarget.setAttribute("aria-expanded", "true")
    this.positionPanel()
    requestAnimationFrame(() => this.positionPanel())
    this.setupPanelListeners()
  }

  closePanel({ restoreFocus = false } = {}) {
    if (!this.hasPanelTarget) return
    this.panelTarget.classList.add("is-closed")
    if (this.hasToggleTarget) this.toggleTarget.setAttribute("aria-expanded", "false")
    this.teardownPanelListeners()
    if (restoreFocus && this.hasToggleTarget) this.toggleTarget.focus()
  }

  panelOpen() {
    return this.hasPanelTarget && !this.panelTarget.classList.contains("is-closed")
  }

  setupPanelListeners() {
    window.addEventListener("resize", this.repositionHandler)
    window.addEventListener("scroll", this.repositionHandler, true)
    window.addEventListener("keydown", this.escapeHandler)
    this.clickOutsideTimer = window.setTimeout(() => {
      document.addEventListener("click", this.clickOutsideHandler)
    }, 0)
  }

  teardownPanelListeners() {
    window.clearTimeout(this.clickOutsideTimer)
    document.removeEventListener("click", this.clickOutsideHandler)
    window.removeEventListener("keydown", this.escapeHandler)
    if (!this.repositionHandler) return
    window.removeEventListener("resize", this.repositionHandler)
    window.removeEventListener("scroll", this.repositionHandler, true)
  }

  repositionIfOpen() {
    if (!this.panelOpen()) return
    this.positionPanel()
  }

  positionPanel() {
    const icon = this.toggleTarget.querySelector("i") || this.toggleTarget
    const anchorRect = icon.getBoundingClientRect()
    const panel = this.panelTarget
    const panelWidth = panel.offsetWidth
    const panelHeight = panel.offsetHeight
    const gap = 4

    let left = anchorRect.left + (anchorRect.width / 2) - (panelWidth / 2)
    if (left + panelWidth > window.innerWidth - gap) {
      left = window.innerWidth - panelWidth - gap
    }
    if (left < gap) left = gap

    let top = anchorRect.bottom + gap
    if (top + panelHeight > window.innerHeight - gap && anchorRect.top - panelHeight - gap >= gap) {
      top = anchorRect.top - panelHeight - gap
    }

    // left/top are viewport coordinates from getBoundingClientRect; no CSS class can express them.
    panel.style.left = `${left}px`
    panel.style.top = `${top}px`
  }
}
